import { useState } from 'react';
import toast from 'react-hot-toast';
import InputField from 'components/fields/InputField';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const AddInstallment = ({
  studentId,
  onCancel,
  onSave,
  remainingAmount,
  existingInstallments,
  isSwapPayment,
  course,
  batch,
}) => {
  const [dueDate, setDueDate] = useState('');
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const validateAmount = (value) => {
    const numValue = Number(value);
    if (numValue <= 0) {
      toast.error('Amount cannot be 0 or negative');
      return false;
    }
    if (numValue < 1000) {
      toast.error('Amount cannot be less than 1000');
      return false;
    }
    if (numValue > remainingAmount) {
      toast.error(
        `Amount cannot exceed remaining balance of ₹${remainingAmount}`
      );
      return false;
    }
    return true;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const handleInitialValidation = () => {
    if (!dueDate) {
      toast.error('Please select a valid date');
      return false;
    }

    const selectedDate = new Date(dueDate);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error('Due date cannot be in the past');
      return false;
    }

    if (!amount || !validateAmount(Number(amount))) {
      return false;
    }

    // Check for duplicate date
    const isDuplicateDate = existingInstallments.some(
      (inst) => new Date(inst.dueDate).toISOString().split('T')[0] === dueDate
    );

    if (isDuplicateDate) {
      toast.error('An installment already exists for this date');
      return false;
    }

    return true;
  };

  const handleSaveClick = () => {
    if (isSubmitting) return;

    if (handleInitialValidation()) {
      setShowConfirmation(true);
    }
  };

  const handleConfirmSave = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const newInstallment = {
        studentId,
        amount: Number(amount),
        dueDate,
        isSwapPayment,
        course: course.id,
        batch: batch.id,
      };

      const success = await onSave(newInstallment);

      if (success) {
        setShowConfirmation(false);
      }
    } catch (error) {
      toast.error('Failed to add installment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:bg-navy-800">
      <h3 className="mb-4 text-lg font-medium text-gray-800 dark:text-white">
        Add New {isSwapPayment ? 'Swap ' : ''}Installment
      </h3>

      <div className="space-y-4">
        <div>
          <InputField
            label="Course"
            type="text"
            value={course?.name || ''}
            readOnly
            className="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm"
          />
        </div>

        <div>
          <InputField
            label="Batch"
            type="text"
            value={batch?.batchNumber || ''}
            readOnly
            className="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm"
          />
        </div>
        <div>
          <InputField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            disabled={isSubmitting}
            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>

        <div>
          <InputField
            label="Amount"
            type="text"
            value={amount}
            onChange={handleAmountChange}
            disabled={isSubmitting}
            placeholder="Enter amount (minimum 1000)"
            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <p className="mt-1 text-xs text-gray-500">
            Available balance: ₹{remainingAmount} (Minimum installment: ₹1,000)
          </p>
        </div>

        <div className="flex gap-2 pt-4">
          <button
            onClick={handleSaveClick}
            disabled={isSubmitting}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-200 ${
              isSubmitting
                ? 'cursor-not-allowed bg-blue-400'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Adding...' : 'Add Installment'}
          </button>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmSave}
        title="Confirm New Installment"
        message={`Are you sure you want to add a new ${
          isSwapPayment ? 'swap ' : ''
        }installment of ₹${amount} due on ${new Date(
          dueDate
        ).toLocaleDateString()}?`}
        confirmText="Add Installment"
        cancelText="Cancel"
        icon="info"
      />
    </div>
  );
};

export default AddInstallment;
